// =============================================================================
// MeuDriver — Hook: usePackages
// Lista pacotes disponíveis e pacotes ativos do aluno
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import type { LessonPackageRow, StudentPackageRow } from '@/types/database'

export function useLessonPackages() {
  return useQuery<LessonPackageRow[]>({
    queryKey: ['lesson-packages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lesson_packages')
        .select('*')
        .eq('active', true)
        .order('lessons_count')
      if (error) throw error
      return (data ?? []) as LessonPackageRow[]
    },
    staleTime: 1000 * 60 * 10,
  })
}

export function useStudentPackages() {
  const { user } = useAuth()

  return useQuery<StudentPackageRow[]>({
    queryKey: ['student-packages', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('student_packages')
        .select('*, package:lesson_packages(*)')
        .eq('student_id', user!.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as unknown as StudentPackageRow[]
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60,
  })
}

export function useActiveStudentPackage() {
  const { user } = useAuth()

  return useQuery<(StudentPackageRow & { package: LessonPackageRow }) | null>({
    queryKey: ['active-student-package', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('student_packages')
        .select('*, package:lesson_packages(*)')
        .eq('student_id', user!.id)
        .eq('status', 'active')
        .gt('lessons_remaining', 0)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (error) throw error
      return data as (StudentPackageRow & { package: LessonPackageRow }) | null
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60,
  })
}

export function usePurchasePackage() {
  const qc = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (params: { package_id: string; instructor_id?: string }) => {
      const { data: sessionData } = await supabase.auth.getSession()
      const jwt = sessionData.session?.access_token
      if (!jwt) throw new Error('Sessão expirada. Faça login novamente.')

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/purchase-package`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(params),
        },
      )
      const result = await res.json()
      if (!res.ok) throw new Error(result.message ?? result.error ?? 'Erro ao processar compra.')
      return result as {
        student_package_id: string
        preference_id: string
        init_point: string
        sandbox_init_point: string
      }
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['student-packages', user?.id] })
      qc.invalidateQueries({ queryKey: ['active-student-package', user?.id] })
      // Redireciona para o checkout do Mercado Pago
      window.location.href = data.init_point
    },
    onError: (err: Error) => {
      toast.error(err.message)
    },
  })
}
